import PropTypes from 'prop-types';
import React, {
  createContext, useContext, useReducer, useCallback, useMemo,
} from 'react';

import { defaultSearchArgs, emptySearchFilters } from '../utils/settings_builder';

const initialContext = {};
const StateContext = createContext(initialContext);
const DispatchContext = createContext(undefined);

function reducer(state, action) {
  switch (action.type) {
  case 'clearSearchState': {
    return {
      ...state,
      ...defaultSearchArgs,
    };
  }
  case 'setQuery': {
    return { ...state, page: 0, query: action.query };
  }
  case 'setPage': {
    return { ...state, page: action.page };
  }
  case 'setSortOrder': {
    return { ...state, sortOrder: action.sortOrder };
  }
  case 'setBrand': {
    return { ...state, brand: action.brand };
  }
  case 'setCategory': {
    return {
      ...state,
      category: action.category,
      customCategory: '',
    };
  }
  case 'setCustomCategory': {
    return {
      ...state,
      customCategory: action.customCategory,
      category: '',
    };
  }
  case 'setSubCategory': {
    return { ...state, subCategory: action.subCategory };
  }
  case 'setMerchantIds': {
    return { ...state, merchantIds: action.merchantIds };
  }
  case 'setResultCount': {
    return { ...state, resultCount: action.resultCount };
  }
  case 'setOnSaleOnly': {
    return { ...state, onSaleOnly: action.onSaleOnly };
  }
  case 'setOnSaleLevel': {
    return { ...state, onSaleLevel: action.onSaleLevel };
  }
  case 'setMinPrice': {
    return { ...state, minPrice: action.minPrice };
  }
  case 'setMaxPrice': {
    return { ...state, maxPrice: action.maxPrice };
  }
  case 'setDepartment': {
    return { ...state, department: action.department };
  }
  case 'setMerchantCategoryIds': {
    return { ...state, merchantCategoryIds: action.merchantCategoryIds };
  }
  case 'setAvantlinkAffiliateId': {
    return { ...state, avantlinkAffiliateId: action.avantlinkAffiliateId };
  }
  case 'setAvantlinkWebsiteId': {
    return { ...state, avantlinkWebsiteId: action.avantlinkWebsiteId };
  }
  case 'setSearchUrl': {
    return { ...state, searchUrl: action.searchUrl };
  }
  case 'setSearchHeaders': {
    return { ...state, searchHeaders: action.searchHeaders };
  }
  case 'clearFilters': {
    return {
      ...state,
      ...emptySearchFilters,
    };
  }
  default:
    throw new Error('Invalid type');
  }
}

export function SearchContextProvider(props) {
  const { children, settings } = props;
  const [state, dispatch] = useReducer(reducer, { ...defaultSearchArgs, ...settings });
  return (
    <StateContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>
        {children}
      </DispatchContext.Provider>
    </StateContext.Provider>
  );
}

SearchContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  settings: PropTypes.shape({
    query: PropTypes.string,
    page: PropTypes.number,
    sortOrder: PropTypes.string,
    brand: PropTypes.string,
    category: PropTypes.string,
    subCategory: PropTypes.string,
    merchantIds: PropTypes.arrayOf(PropTypes.number),
    onSaleOnly: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    onSaleLevel: PropTypes.number,
    minPrice: PropTypes.number,
    maxPrice: PropTypes.number,
    department: PropTypes.string,
    merchantCategoryIds: PropTypes.arrayOf(PropTypes.number),
    avantlinkAffiliateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    avantlinkWebsiteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    searchUrl: PropTypes.string,
    searchHeaders: PropTypes.shape({}),
  }),
};

SearchContextProvider.defaultProps = {
  settings: {},
};

export function useSearchState() {
  return useContext(StateContext);
}

export function useSearchDispatch() {
  const dispatch = useContext(DispatchContext);

  if (dispatch === undefined) {
    throw new Error('useSearchDispatch must be used within a SearchContextProvider');
  }

  const setQuery = useCallback((query) => {
    dispatch({ type: 'setQuery', query });
  }, [dispatch]);

  const setPage = useCallback((page) => {
    dispatch({ type: 'setPage', page });
  }, [dispatch]);

  const setSortOrder = useCallback((sortOrder) => {
    dispatch({ type: 'setSortOrder', sortOrder });
  }, [dispatch]);

  const setBrand = useCallback((brand) => {
    dispatch({ type: 'setBrand', brand });
  }, [dispatch]);

  const setCategory = useCallback((category) => {
    dispatch({ type: 'setCategory', category });
  }, [dispatch]);

  const setCustomCategory = useCallback((customCategory) => {
    dispatch({ type: 'setCustomCategory', customCategory });
  }, [dispatch]);

  const setSubCategory = useCallback((subCategory) => {
    dispatch({ type: 'setSubCategory', subCategory });
  }, [dispatch]);

  const setMerchantIds = useCallback((merchantIds) => {
    dispatch({ type: 'setMerchantIds', merchantIds });
  }, [dispatch]);

  const clearSearchState = useCallback(() => {
    dispatch({ type: 'clearSearchState' });
  }, [dispatch]);

  const setResultCount = useCallback((resultCount) => {
    dispatch({ type: 'setResultCount', resultCount: parseInt(resultCount, 10) });
  }, [dispatch]);

  const setOnSaleOnly = useCallback((onSaleOnly) => {
    dispatch({ type: 'setOnSaleOnly', onSaleOnly });
  }, [dispatch]);

  const setOnSaleLevel = useCallback((onSaleLevel) => {
    dispatch({ type: 'setOnSaleLevel', onSaleLevel });
  }, [dispatch]);

  const setMinPrice = useCallback((minPrice) => {
    dispatch({ type: 'setMinPrice', minPrice });
  }, [dispatch]);

  const setMaxPrice = useCallback((maxPrice) => {
    dispatch({ type: 'setMaxPrice', maxPrice });
  }, [dispatch]);

  const setDepartment = useCallback((department) => {
    dispatch({ type: 'setDepartment', department });
  }, [dispatch]);

  const setMerchantCategoryIds = useCallback((merchantCategoryIds) => {
    dispatch({ type: 'setMerchantCategoryIds', merchantCategoryIds });
  }, [dispatch]);

  const setAvantlinkAffiliateId = useCallback((avantlinkAffiliateId) => {
    dispatch({ type: 'setAvantlinkAffiliateId', avantlinkAffiliateId });
  }, [dispatch]);

  const setAvantlinkWebsiteId = useCallback((avantlinkWebsiteId) => {
    dispatch({ type: 'setAvantlinkWebsiteId', avantlinkWebsiteId });
  }, [dispatch]);

  const setSearchUrl = useCallback((searchUrl) => {
    dispatch({ type: 'setSearchUrl', searchUrl });
  }, [dispatch]);

  const setSearchHeaders = useCallback((searchHeaders) => {
    dispatch({ type: 'setSearchHeaders', searchHeaders });
  }, [dispatch]);

  const clearFilters = useCallback(() => {
    dispatch({ type: 'clearFilters' });
  }, [dispatch]);

  return useMemo(
    () => ({
      setQuery,
      setPage,
      setSortOrder,
      setBrand,
      setCategory,
      setCustomCategory,
      setSubCategory,
      setMerchantIds,
      clearSearchState,
      setResultCount,
      setOnSaleOnly,
      setOnSaleLevel,
      setMinPrice,
      setMaxPrice,
      setDepartment,
      setMerchantCategoryIds,
      setAvantlinkAffiliateId,
      setAvantlinkWebsiteId,
      setSearchUrl,
      setSearchHeaders,
      clearFilters,
    }),
    [dispatch],
  );
}
